import { useState } from 'react'
import { FormikConfig } from 'formik'
import { notification } from '@/shared/helpers/notificationMessages'
import { errorHandler } from '@/shared/lib/errorHandler'
import { useTranslation } from 'react-i18next'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { hasAiTranslation } from '@/shared/lib/utils'
import { initializeTranslationsForCreation } from '@/shared/lib/initializeTranslationsForCreation'
import { createWebinarFormDefault } from './consts'
import { useCreateWebinarApi } from '../api/create-webinar-form-api'
import { CreateWebinarFormState } from '../model/types'

type UseCreateWebinarFormProps = {
  onCloseModal: () => void
}

export const useCreateWebinarForm = (props: UseCreateWebinarFormProps) => {
  const { onCloseModal } = props
  const { language } = useLanguageStore()
  const { t } = useTranslation(['common'])

  const [initialValues, setInitialValues] = useState<CreateWebinarFormState>(
    () => createWebinarFormDefault,
  )
  const { mutateAsync, isPending: isLoadingCreateWebinar } = useCreateWebinarApi()

  const submitForm: FormikConfig<CreateWebinarFormState>['onSubmit'] = async values => {
    const { translations, ...restValues } = values
    const updatedTranslations = initializeTranslationsForCreation(translations)
    const hasAiTranslationFlag = hasAiTranslation(updatedTranslations)

    const updatedValues = {
      ...restValues,
      has_ai_translation: hasAiTranslationFlag,
      translations: updatedTranslations,
    }

    onCloseModal()
    notification.loading(
      t('common:notification_titles.please_wait'),
      t('common:notification_messages.create_webinar_in_progress'),
    )

    try {
      await mutateAsync(updatedValues)
      notification.success(
        t('common:notification_titles.success'),
        t('common:notification_messages.create_webinar_success'),
      )
    } catch (error) {
      errorHandler(error, t('common:notification_messages.create_webinar_error'))
    }
  }

  return {
    setInitialValues,
    initialValues,
    isLoadingCreateWebinar,
    submitForm,
    createWebinarFormDefault,
  }
}
